/**
 * Vault Breaker Commissions — productized custom-game build service.
 *
 * Three tiers (Reskin / Variant / Bespoke). 10% revshare on every
 * deployed game, regardless of tier — same deal as our flagship. The
 * upfront fee is a commitment deposit, not a margin source.
 *
 * Source of truth for the /commission landing route. Server-side intake
 * (Agent 3) reads COMMISSION_INTAKE_FIELDS to drive the form schema; do
 * not rename keys without coordinating that handoff.
 */

export type CommissionTierId = "reskin" | "variant" | "bespoke";

export interface CommissionTier {
  /** Stable id used by the contact form, analytics, and deep links. */
  id: CommissionTierId;
  /** Human-facing tier name. Title-case, no marketing adjectives. */
  name: string;
  /** Headline price. Strings, not numbers — some tiers carry "from". */
  price: string;
  /** SLA measured in business days, server-side trigger for refund logic. */
  slaDays: number;
  /** SLA copy for display ("5 business days"). */
  slaLabel: string;
  /** One-line summary used in cards and the meta description. */
  summary: string;
  /** Concrete deliverables this tier ships. Render as a check list. */
  includes: string[];
  /** Honest "out of scope" surface — render with a strikethrough/X. */
  notIncludes: string[];
  /** Buy-out price to drop the 10% revshare on this tier. */
  buyoutFrom: string;
  /** Card affordance — only one tier should be flagged recommended. */
  recommended?: boolean;
}

export const COMMISSION_TIERS: CommissionTier[] = [
  {
    id: "reskin",
    name: "Reskin",
    price: "$249",
    slaDays: 5,
    slaLabel: "5 business days",
    summary:
      "Pick one of our three base templates. We rebrand it with your colours, logo, name, and in-game flavour copy. Math is unchanged and audited.",
    includes: [
      "Choice of base template — Vault Breaker, Pressure Plate, or Cipher Crack",
      "Operator brand pass — palette, logo, typography, in-game flavour copy",
      "Loading screen, end-of-round panel, and lobby tile assets",
      "Built on the production engine — provably fair, same RTP, same audit",
      "Sandbox build on day 4 for sign-off, production handoff on day 5",
      "Free patch window — 14 days post-launch for cosmetic fixes",
    ],
    notIncludes: [
      "Custom math (use the Variant tier)",
      "New mechanic or new game loop (use the Bespoke tier)",
      "Original audio composition (we ship our existing pack)",
      "Native iOS or Android wrapper (browser only)",
    ],
    buyoutFrom: "$9,999",
    recommended: true,
  },
  {
    id: "variant",
    name: "Variant",
    price: "$999",
    slaDays: 7,
    slaLabel: "7 business days",
    summary:
      "Reskin plus a custom math variant. Different RTP within 90–99%, custom payout ladder, or a mechanic tweak — for example three vault locks instead of five.",
    includes: [
      "Everything in the Reskin tier",
      "Custom RTP within the 90–99% band, with published math",
      "Custom payout ladder, tuned with you against your player base",
      "One mechanic tweak — for example, lock count, round length, or tick rate",
      "Updated test suite proving the new RTP at 1M-round scale",
      "Variant-specific provably-fair seed schema, documented",
    ],
    notIncludes: [
      "Brand-new mechanic or never-before-seen game loop (use Bespoke)",
      "Original audio composition",
      "Native iOS or Android wrapper",
    ],
    buyoutFrom: "$24,999",
  },
  {
    id: "bespoke",
    name: "Bespoke",
    price: "from $2,999",
    slaDays: 14,
    slaLabel: "14 business days",
    summary:
      "New mechanic, new math, new visuals. Quoted per project after a 30-minute scoping call. We only take on bespoke work we believe will earn its revshare.",
    includes: [
      "Original game design — mechanic, pacing, payout structure",
      "Custom math model with published RTP and house-edge derivation",
      "Original visual treatment — not a reskin of an existing template",
      "Same provably-fair seed commit/reveal as the rest of our catalogue",
      "Two preview builds during the 14-day window for shaping the feel",
      "Same RGS surface — drops into your existing integration with no new endpoints",
    ],
    notIncludes: [
      "Concepts we don't believe will clear their revshare hurdle (we'll say so)",
      "Original orchestral score (separate quote if needed)",
      "Native iOS or Android wrapper",
    ],
    buyoutFrom: "Quoted per project",
  },
];

export interface CommissionPipelineStep {
  /** Day index. 0 = intake (kick-off call). */
  day: number;
  /** Display label, e.g. "Day 0" or "Day 5". */
  label: string;
  /** Short stage name. */
  title: string;
  /** Two-sentence description of what we do and what you do that day. */
  description: string;
}

/**
 * The Reskin pipeline. Variant adds two days for math iteration; Bespoke
 * runs over 14 days with the same shape but more iteration loops on
 * days 2–10. We surface the Reskin timeline because it's the tightest
 * and most representative of how the studio actually ships.
 */
export const COMMISSION_PIPELINE: CommissionPipelineStep[] = [
  {
    day: 0,
    label: "Day 0",
    title: "Intake",
    description:
      "30-minute call. You bring your brand, your jurisdiction, and the template you want. We confirm scope, sign the one-page SOW, and invoice the deposit.",
  },
  {
    day: 1,
    label: "Day 1",
    title: "Scope lock",
    description:
      "We send a written scope doc — assets we need from you, copy strings, palette, logo files. Once you sign off, scope is frozen. Out-of-scope changes after this point bump the SLA.",
  },
  {
    day: 2,
    label: "Day 2",
    title: "Build",
    description:
      "Brand pass on the chosen template. Palette, typography, logo placement, in-game copy, end-of-round panel, lobby tile. Engine and math untouched.",
  },
  {
    day: 3,
    label: "Day 3",
    title: "QA",
    description:
      "Internal QA pass — provably-fair verification, mobile breakpoints, asset bleed, copy polish. Bug list resolved before sign-off build.",
  },
  {
    day: 4,
    label: "Day 4",
    title: "Preview + review",
    description:
      "Sandbox build delivered to your team for review. You play it on your devices, send notes, we land the cosmetic round of fixes the same day.",
  },
  {
    day: 5,
    label: "Day 5",
    title: "Production + handoff",
    description:
      "Production build issued. Same RGS surface as our catalogue — your existing integration picks it up by game id. We hand off the asset bundle and the patch-window contact.",
  },
];

export interface CommissionFaqEntry {
  q: string;
  a: string;
}

export const COMMISSION_FAQ: CommissionFaqEntry[] = [
  {
    q: "Why $249 if it doesn't cover your cost?",
    a: "Because we want skin in the game on both sides. The deposit is a commitment signal — operators who pay $249 actually launch the build, operators who pay nothing usually don't. Our real revenue is the 10% revshare on the deployed game; if the build never goes live, neither party earned anything. The deposit just keeps the calendar honest.",
  },
  {
    q: "Do you also take revshare on commissioned games?",
    a: "Yes — 10% of GGR on the deployed game, settled weekly in USDT or USDC. Same deal as our flagship and our Pressure Plate / Cipher Crack catalogue. The build fee and the revshare are independent.",
  },
  {
    q: "What happens if you miss the SLA?",
    a: "Build fee refunded in full, no questions. SLA clock starts on the day you sign off the scope doc (Day 1), not the day you send the brief. Out-of-scope change requests pause the clock — we'll tell you in writing when that happens.",
  },
  {
    q: "Can I buy out the revshare?",
    a: "Yes. Buy-out starts at $9,999 for the Reskin tier and $24,999 for Variant. Bespoke is quoted per project. Buy-out is a one-time payment that drops the 10% in perpetuity — useful for operators who plan to scale a single title hard. Most operators don't; the revshare is usually cheaper.",
  },
  {
    q: "Which jurisdictions do you serve?",
    a: "All of them — if your operator license lets you offer crypto casino games to your players, we'll commission for you. We don't add jurisdictional restrictions on top of yours. Your license, your call.",
  },
  {
    q: "Can I bring my own art?",
    a: "Yes — and we encourage it. If you ship us a polished asset pack (logo, palette, illustrations) on Day 1, we ship faster and the result looks like your brand, not ours. If you don't, our in-house art will do a clean job, but it's a brand reskin, not a custom illustration project.",
  },
  {
    q: "What about audio?",
    a: "We ship our existing audio pack on every reskin and variant. It's clean, original, and royalty-clear. Custom composition (musical theme, custom SFX library) is out of scope for the productized tiers — happy to quote it as a Bespoke add-on if you need it.",
  },
  {
    q: "Is there a mobile native build?",
    a: "No. All our games are browser-first, designed to run inside an iframe in your existing casino lobby. They scale to any aspect ratio from desktop to mobile portrait without a separate app. If you specifically need a wrapped iOS or Android binary, talk to us — it's not impossible, just not what these tiers cover.",
  },
  {
    q: "What math constraints apply to a Variant build?",
    a: "RTP must land in the 90–99% band. We won't ship a 99.5% game (we don't believe operators actually want that) or anything below 90% (we don't believe players should). Beyond that, we're flexible on payout ladder, volatility, and round length. We publish the math you ship and back it with the same engine test suite as our catalogue.",
  },
  {
    q: "What if I want a game that doesn't fit any of these tiers?",
    a: "That's the Bespoke tier — quoted per project after a 30-minute scoping call. We're selective: we only take on bespoke work we believe will clear its 10% revshare hurdle. If we don't think your concept will earn, we'll tell you and point you at one of the productized tiers instead.",
  },
];

export interface CommissionIntakeField {
  /** Stable form key — used by the server endpoint. */
  key: string;
  /** Display label for the form. */
  label: string;
  /** UI hint about the input type — guides Agent 3's form rendering. */
  kind: "text" | "longtext" | "select" | "url" | "email";
  /** Whether the operator must fill this in to submit. */
  required: boolean;
  /** Helper text rendered under the field. */
  hint?: string;
}

/**
 * Canonical intake field list. Agent 3's contact-form endpoint reads
 * this array to validate the submission. Adding a field here is the
 * supported way to extend the brief — do not add fields client-side
 * only, the server will reject anything not listed here.
 */
export const COMMISSION_INTAKE_FIELDS: CommissionIntakeField[] = [
  {
    key: "companyName",
    label: "Company name",
    kind: "text",
    required: true,
  },
  {
    key: "contactEmail",
    label: "Contact email",
    kind: "email",
    required: true,
  },
  {
    key: "operatorJurisdiction",
    label: "Licensing jurisdiction",
    kind: "text",
    required: true,
    hint: "Where you're licensed today. Used so we can tailor copy and compliance to your market.",
  },
  {
    key: "tier",
    label: "Tier",
    kind: "select",
    required: true,
    hint: "Reskin / Variant / Bespoke.",
  },
  {
    key: "baseTemplate",
    label: "Base template",
    kind: "select",
    required: false,
    hint: "Reskin and Variant only — Vault Breaker, Pressure Plate, or Cipher Crack.",
  },
  {
    key: "targetRtp",
    label: "Target RTP",
    kind: "text",
    required: false,
    hint: "Variant tier only. Must land between 90% and 99%.",
  },
  {
    key: "brandAssetsUrl",
    label: "Brand assets URL",
    kind: "url",
    required: false,
    hint: "Drive, Figma, or zip link with logo, palette, and any reference art.",
  },
  {
    key: "gameBrief",
    label: "Game brief",
    kind: "longtext",
    required: true,
    hint: "Two paragraphs is fine. What does the game feel like, who's it for, what's your operator angle?",
  },
  {
    key: "deadline",
    label: "Deadline (optional)",
    kind: "text",
    required: false,
    hint: "If you've got a marketing date, tell us — we'll either commit to it in writing or decline.",
  },
];

/** Doc-friendly export — one source of truth for the field labels. */
export const COMMISSION_INTAKE_FIELD_LABELS: string[] = COMMISSION_INTAKE_FIELDS.map(
  (f) => f.label,
);
